import { useState } from 'react';
import { Link } from 'react-router-dom';
import { List, Plus, Settings2, Sparkles } from 'lucide-react';
import { PageHeader, ErrorState } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, Skeleton } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useLeadBoard, useMoveLead, usePipelines } from './api';
import { LEAD_SOURCE_LABELS } from './labels';
import { LeadDialog } from './lead-dialog';
import { ConvertLeadDialog } from './convert-lead-dialog';
import type { Lead, LeadBoardColumn } from './types';

/**
 * The lead board.
 *
 * The same Kanban as the deal pipeline, on the lead pipelines — a lead is
 * answered, chased and qualified long before it has a value or a close date,
 * so the columns are different and so is what a card shows.
 */
export function LeadsBoardPage() {
  const { data: pipelines = [] } = usePipelines('lead');
  const [pipelineId, setPipelineId] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [converting, setConverting] = useState<Lead | null>(null);
  const [dragging, setDragging] = useState<Lead | null>(null);

  const debounced = useDebounce(search, 300);
  const { data, isLoading, error, refetch } = useLeadBoard({
    pipelineId: pipelineId || undefined,
    ownerId: ownerFilter || undefined,
    q: debounced || undefined,
  });
  const move = useMoveLead();

  const onDrop = (column: LeadBoardColumn) => {
    const lead = dragging;
    setDragging(null);
    if (!lead || lead.stageId === column.stage.id) return;
    move.mutate({ id: lead.id, stageId: column.stage.id });
  };

  return (
    <div>
      <PageHeader
        title="Lead pipeline"
        description="Drag a lead to the column it belongs in. Qualified and dead-end columns set the lead's status with it."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/crm/leads">
                <List className="h-4 w-4" />
                List view
              </Link>
            </Button>
            <Can permission="crm.lead.create">
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New lead
              </Button>
            </Can>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          value={pipelineId}
          onChange={(event) => setPipelineId(event.target.value)}
          className="h-9 w-auto min-w-[12rem]"
        >
          <option value="">Default board</option>
          {pipelines.map((pipeline) => (
            <option key={pipeline.id} value={pipeline.id}>
              {pipeline.name}
              {pipeline.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </Select>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search leads…"
          className="h-9 w-auto min-w-[12rem]"
        />
        <Select
          value={ownerFilter}
          onChange={(event) => setOwnerFilter(event.target.value)}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">Everyone</option>
          {owners(data?.columns).map((owner) => (
            <option key={owner.id} value={owner.id}>
              {owner.name}
            </option>
          ))}
        </Select>
        <Can permission="crm.pipeline.create">
          <Button asChild variant="ghost" size="sm">
            <Link to="/crm/pipelines/lead">
              <Settings2 className="h-4 w-4" />
              Lead pipeline setup
            </Link>
          </Button>
        </Can>

        {data && (
          <div className="ml-auto text-sm text-muted-foreground">
            <span className="font-medium text-foreground">
              {data.columns.reduce((sum, column) => sum + column.count, 0)}
            </span>{' '}
            leads on this board
          </div>
        )}
      </div>

      {error ? (
        <ErrorState message="Could not load the lead board." onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex gap-3 overflow-x-auto">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-96 w-[calc(100%-2.5rem)] shrink-0 md:w-72" />
          ))}
        </div>
      ) : (
        <div className="scrollbar-thin flex snap-x snap-mandatory gap-3 overflow-x-auto pb-4 md:snap-none">
          {data?.columns.map((column) => (
            <div
              key={column.stage.id}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => onDrop(column)}
              className={cn(
                // One stage per swipe on a phone: the column fills the scroller except
                // for a sliver of the next, and the scroll snaps to it. On a desktop
                // the columns are narrow again and snapping would fight the drag.
                'flex w-[calc(100%-2.5rem)] shrink-0 snap-start flex-col rounded-lg border bg-muted/30 transition-colors md:w-72',
                dragging && dragging.stageId !== column.stage.id && 'border-primary/40 bg-primary/5',
              )}
            >
              <div className="flex items-center gap-2 border-b px-3 py-2.5">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: column.stage.colour }}
                />
                <span className="truncate text-sm font-medium">{column.stage.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">{column.count}</span>
              </div>

              <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2">
                {column.leads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    onOpen={() => setEditing(lead)}
                    onConvert={() => setConverting(lead)}
                    onDragStart={() => setDragging(lead)}
                    onDragEnd={() => setDragging(null)}
                  />
                ))}
                {column.leads.length === 0 && (
                  <p className="px-2 py-6 text-center text-xs text-muted-foreground">Nothing here</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && <LeadDialog onClose={() => setCreating(false)} />}
      {editing && <LeadDialog lead={editing} onClose={() => setEditing(null)} />}
      {converting && (
        <ConvertLeadDialog lead={converting} onClose={() => setConverting(null)} />
      )}
    </div>
  );
}

function LeadCard({
  lead,
  onOpen,
  onConvert,
  onDragStart,
  onDragEnd,
}: {
  lead: Lead;
  onOpen: () => void;
  onConvert: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className="cursor-grab rounded-md border bg-background p-2.5 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="line-clamp-2 text-sm font-medium">{lead.name}</span>
        <span className="shrink-0 text-[10px] text-muted-foreground">{lead.number}</span>
      </div>
      {lead.companyName && (
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{lead.companyName}</p>
      )}
      {(lead.phone || lead.email) && (
        <p className="mt-1 truncate text-xs text-muted-foreground">{lead.phone ?? lead.email}</p>
      )}
      <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="truncate">{LEAD_SOURCE_LABELS[lead.source] ?? lead.source}</span>
        <span className="ml-auto shrink-0">{timeAgo(lead.updatedAt)}</span>
      </div>
      <div className="mt-2 flex items-center gap-2">
        {lead.owner && (
          <span className="truncate text-[11px] text-muted-foreground">{lead.owner.name}</span>
        )}
        {lead.status !== 'CONVERTED' && (
          <Can permission="crm.lead.update">
            <button
              onClick={(event) => {
                event.stopPropagation();
                onConvert();
              }}
              className="ml-auto flex shrink-0 items-center gap-1 text-[11px] text-primary hover:underline"
            >
              <Sparkles className="h-3 w-3" />
              Convert
            </button>
          </Can>
        )}
      </div>
    </div>
  );
}

/** The owners with leads on this board, for the filter. */
function owners(columns: LeadBoardColumn[] | undefined) {
  const found = new Map<string, { id: string; name: string }>();
  for (const column of columns ?? []) {
    for (const lead of column.leads) {
      if (lead.owner) found.set(lead.owner.id, { id: lead.owner.id, name: lead.owner.name });
    }
  }
  return [...found.values()].sort((a, b) => a.name.localeCompare(b.name));
}
